import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useValidationBreakList } from "../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useState, useEffect } from "react";
import { TableAction, TableColumn } from "@shared/types/table";
import { GenericTable } from "@shared/components/GenericTable";
import { ArrowDownToLine, Eye } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuditUsers } from "@features/mx/validations/hooks";
import { useUserUtilityServices } from "@features/mx/settings/hooks";
import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { FormField } from "@shared/services/FormServices";
import CycleFilters from "./ActiveCycles/CycleFilters";
import { Button } from "@shared/ui/button";

function CompletedReadCycles() {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get filters from URL
  const urlPage = Number(searchParams.get("page") || "1");
  const urlStartDate = searchParams.get("start_date") || "";
  const urlEndDate = searchParams.get("end_date") || "";
  const urlMeterType = searchParams.get("meter_type") || "";
  const urlUtilityService = searchParams.get("utility_service") || "";
  const urlValidatorId = searchParams.get("validator_id") || "";
  const urlSupervisorId = searchParams.get("supervisor_id") || "";

  const [page, setPage] = useState(urlPage);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<any>({
    start_date: urlStartDate,
    end_date: urlEndDate,
    meter_type: urlMeterType,
    utility_service: urlUtilityService,
    validator_id: urlValidatorId,
    supervisor_id: urlSupervisorId,
  });

  const limit = PAGE_SIZE;
  const isCompleted = true;

  const { data: completedCycle } = useValidationBreakList({
    remoteUtilityId,
    page,
    limit,
    isCompleted: true,
    filters,
  });

  // Get dropdown data
  const { data: utilityServices } = useUserUtilityServices(remoteUtilityId);
  const { data: readTypes } = useChoices({ choice: "meter_type" });
  const { data: validatorsList } = useAuditUsers(
    remoteUtilityId,
    0, // validator = 0
    showFilters,
  );
  const { data: supervisorList } = useAuditUsers(
    remoteUtilityId,
    2, // supervisor = 2
    showFilters,
  );

  const validatorDropdownOptions = mapToLabelValue(
    validatorsList,
    "name",
    "id",
  );
  const supervisorDropdownOptions = mapToLabelValue(
    supervisorList,
    "name",
    "id",
  );

  const activeUtilityServices = utilityServices?.filter(
    (service) => service.isActive,
  );
  const utilityServicesList = mapToLabelValue(
    activeUtilityServices,
    "name",
    "name",
  );
  const readTypeList = mapToLabelValue(readTypes, "value", "key");

  // Sync filters with URL params on mount and URL changes
  useEffect(() => {
    setFilters({
      start_date: urlStartDate,
      end_date: urlEndDate,
      meter_type: urlMeterType,
      utility_service: urlUtilityService,
      validator_id: urlValidatorId,
      supervisor_id: urlSupervisorId,
    });
    setPage(urlPage);
  }, [
    urlStartDate,
    urlEndDate,
    urlMeterType,
    urlUtilityService,
    urlValidatorId,
    urlSupervisorId,
    urlPage,
  ]);

  const filterFields: FormField[] = [
    { label: "Start Date", name: "start_date", type: "datepicker" },
    { label: "End Date", name: "end_date", type: "datepicker" },
    {
      label: "Read Type",
      name: "meter_type",
      type: "select",
      options: readTypeList,
    },
    {
      label: "Utility",
      name: "utility_service",
      type: "select",
      options: utilityServicesList,
    },
    {
      label: "Validator",
      name: "validator_id",
      type: "multi-select",
      maxSelection: 1,
      closeOnSelect: true,
      options: validatorDropdownOptions,
    },
    {
      label: "Supervisor",
      name: "supervisor_id",
      type: "multi-select",
      maxSelection: 1,
      closeOnSelect: true,
      options: supervisorDropdownOptions,
    },
  ];

  const handleSubmit = (values: any) => {
    const newParams: any = { page: "1", tab: "completed" };

    // ✅ If values is null (cleared)
    if (!values) {
      setSearchParams(newParams);
      setFilters({});
      setPage(1);
      return;
    }

    // ✅ Otherwise, include only non-empty filters
    Object.keys(values).forEach((key) => {
      if (values[key] && values[key] !== "") {
        newParams[key] = values[key];
      }
    });

    setSearchParams(newParams);
    setFilters(values);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(newPage),
    };
    setSearchParams(newParams);
    setPage(newPage);
  };

  const completedCycleHeader: TableColumn[] = [
    {
      key: "meterCycleName",
      header: "Read Cycle",
      sortable: false,
    },
    {
      key: "createdMonth",
      header: "Month",
      sortable: false,
    },
    {
      key: "createdDate",
      header: "Reading Period",
      sortable: false,
      render: (value, row) => {
        const startDate = row.createdDate;
        const endDate = row.validationEnd;

        if (startDate && endDate) {
          return `${startDate} - ${endDate}`;
        } else if (startDate) {
          return startDate;
        } else if (endDate) {
          return endDate;
        }
        return "N/A";
      },
    },
    {
      key: "meterType",
      header: "Read Type",
      sortable: false,
    },
    {
      key: "productCode",
      header: "Utility Service",
      sortable: false,
      render: (value, row) => {
        return row.productCode?.join(", ");
      },
    },
    {
      key: "totalReadingCount",
      header: "Total Readings",
      sortable: false,
    },
    {
      key: "validatedPercentage",
      header: "% Validated",
      sortable: false,
    },
    {
      key: "exemptedPercentage",
      header: "% Estimated",
      sortable: false,
    },
    {
      key: "validatorInfo",
      header: "Validated By",
      sortable: false,
      render: (_, row) => {
        const validators = row.validatorInfo || [];
        const count = validators.length;

        if (count === 0) return "-";

        const names = validators.map((v) => v.validatorName).join(", ");

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default text-sm text-gray-800">
                  {count}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs break-words text-sm">
                {names}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      key: "supervisorInfo",
      header: "Supervised By",
      sortable: false,
      render: (_, row) => {
        const supervisor = row.supervisorInfo || [];
        const count = supervisor.length;

        if (count === 0) return "-";

        const names = supervisor.map((v) => v.supervisorName).join(", ");

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default text-sm text-gray-800">
                  {count}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs break-words text-sm">
                {names}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
  ];

  const actions: TableAction[] = [
    {
      label: "View",
      icon: <Eye />,
      onClick: (row) => {
        navigate(
          `/mx/meter-validation/photometer-validation/view/${row.runId}?tab=pass`,
        );
      },
    },
  ];

  const pagination = {
    currentPage: page,
    totalPages: Math.ceil((completedCycle?.count || 0) / PAGE_SIZE),
    totalItems: completedCycle?.count || 0,
    itemsPerPage: PAGE_SIZE,
    startItem: (page - 1) * PAGE_SIZE + 1,
    endItem: Math.min(page * PAGE_SIZE, completedCycle?.count || 0),
  };
  const FilterPanel = (
    <div className="space-y-4">
      <CycleFilters filterFields={filterFields} onSubmit={handleSubmit} />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-4 pr-2">
        <Button
          variant="outline"
          onClick={() => setShowFilters((prev) => !prev)}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </div>

      {showFilters && FilterPanel}
      <GenericTable
        rowKey="id"
        columns={completedCycleHeader}
        data={completedCycle?.results}
        actions={actions}
        emptyMessage="No data available"
        pagination={pagination}
        onPageChange={handlePageChange}
      />
    </div>
  );
}

export default CompletedReadCycles;
